import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env');
}

// Supabase client - only used for auth
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key'
);

// Helper to check if Supabase is properly configured
export const isSupabaseConfigured = () => {
  return supabaseUrl && supabaseAnonKey &&
         !supabaseUrl.includes('placeholder') &&
         !supabaseAnonKey.includes('placeholder');
};

// ===========================================
// Direct REST API helper (bypasses supabase-js for data fetching)
// ===========================================

interface RestQueryOptions {
  select?: string;
  filters?: Record<string, string | number | boolean | null>;
  order?: { column: string; ascending?: boolean };
  limit?: number;
  offset?: number;
  single?: boolean;
}

async function restFetch<T>(
  table: string,
  options: RestQueryOptions = {}
): Promise<T> {
  const { select = '*', filters = {}, order, limit, offset, single } = options;

  // Build URL with query params
  const params = new URLSearchParams();
  params.set('select', select);

  // Add filters
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null) {
      params.set(key, `eq.${value}`);
    }
  }

  // Add ordering
  if (order) {
    params.set('order', `${order.column}.${order.ascending ? 'asc' : 'desc'}`);
  }

  // Add pagination
  if (limit !== undefined) {
    params.set('limit', String(limit));
  }
  if (offset !== undefined) {
    params.set('offset', String(offset));
  }

  const url = `${supabaseUrl}/rest/v1/${table}?${params.toString()}`;

  const headers: Record<string, string> = {
    'apikey': supabaseAnonKey,
    'Authorization': `Bearer ${supabaseAnonKey}`,
    'Content-Type': 'application/json',
  };

  // Request count in header for pagination
  if (!single) {
    headers['Prefer'] = 'count=exact';
  }

  const response = await fetch(url, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  const data = await response.json();

  if (single) {
    if (Array.isArray(data) && data.length === 0) {
      throw new Error('No rows found');
    }
    return (Array.isArray(data) ? data[0] : data) as T;
  }

  return data as T;
}

// REST API methods
export const rest = {
  from: <T = unknown>(table: string) => ({
    select: (columns = '*') => ({
      _table: table,
      _select: columns,
      _filters: {} as Record<string, string | number | boolean | null>,
      _order: undefined as { column: string; ascending?: boolean } | undefined,
      _limit: undefined as number | undefined,
      _offset: undefined as number | undefined,

      eq(column: string, value: string | number | boolean) {
        this._filters[column] = value;
        return this;
      },

      neq(column: string, value: string | number | boolean) {
        // For neq, we need different URL format
        this._filters[`${column}`] = `neq.${value}` as any;
        return this;
      },

      order(column: string, options?: { ascending?: boolean }) {
        this._order = { column, ascending: options?.ascending ?? false };
        return this;
      },

      limit(count: number) {
        this._limit = count;
        return this;
      },

      range(from: number, to: number) {
        this._offset = from;
        this._limit = to - from + 1;
        return this;
      },

      async single(): Promise<{ data: T | null; error: Error | null }> {
        try {
          const data = await restFetch<T>(this._table, {
            select: this._select,
            filters: this._filters,
            order: this._order,
            limit: 1,
            single: true,
          });
          return { data, error: null };
        } catch (error) {
          return { data: null, error: error as Error };
        }
      },

      async then(resolve: (result: { data: T[] | null; error: Error | null; count?: number }) => void) {
        try {
          const data = await restFetch<T[]>(this._table, {
            select: this._select,
            filters: this._filters,
            order: this._order,
            limit: this._limit,
            offset: this._offset,
          });
          resolve({ data, error: null, count: data.length });
        } catch (error) {
          resolve({ data: null, error: error as Error });
        }
      },
    }),
  }),
};
